import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface CronBuilderProps {
  value: string;
  onChange: (cron: string) => void;
}

const DAYS_OF_WEEK = [
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
  { value: "0", label: "Sun" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i.toString().padStart(2, "0"),
}));

const MINUTES = Array.from({ length: 12 }, (_, i) => ({
  value: String(i * 5),
  label: (i * 5).toString().padStart(2, "0"),
}));

type Frequency = "every_10_minutes" | "hourly" | "daily" | "weekly" | "monthly";

function parseCron(cron: string): { frequency: Frequency; minute: string; hour: string; dayOfMonth: string; daysOfWeek: string[] } {
  const defaults = { frequency: "daily" as Frequency, minute: "0", hour: "9", dayOfMonth: "1", daysOfWeek: ["1"] };
  if (!cron.trim()) return defaults;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return defaults;
  const [min, hr, dom, , dow] = parts;

  if ((min === "*" || min === "*/10") && hr === "*") return { ...defaults, frequency: "every_10_minutes" };
  if (hr === "*") return { ...defaults, frequency: "hourly", minute: min };
  if (dow !== "*") return { frequency: "weekly", minute: min, hour: hr, dayOfMonth: "1", daysOfWeek: dow.split(",") };
  if (dom !== "*") return { frequency: "monthly", minute: min, hour: hr, dayOfMonth: dom, daysOfWeek: ["1"] };
  return { frequency: "daily", minute: min, hour: hr, dayOfMonth: "1", daysOfWeek: ["1"] };
}

function buildCron(frequency: Frequency, minute: string, hour: string, dayOfMonth: string, daysOfWeek: string[]): string {
  switch (frequency) {
    case "every_10_minutes": return "*/10 * * * *";
    case "hourly": return `${minute} * * * *`;
    case "daily": return `${minute} ${hour} * * *`;
    case "weekly": return `${minute} ${hour} * * ${daysOfWeek.length ? daysOfWeek.join(",") : "1"}`;
    case "monthly": return `${minute} ${hour} ${dayOfMonth} * *`;
  }
}

function describeCron(frequency: Frequency, minute: string, hour: string, dayOfMonth: string, daysOfWeek: string[]): string {
  const time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  const dayNames: Record<string, string> = { "0": "Sun", "1": "Mon", "2": "Tue", "3": "Wed", "4": "Thu", "5": "Fri", "6": "Sat" };
  switch (frequency) {
    case "every_10_minutes": return "Runs every 10 minutes";
    case "hourly": return `Runs every hour at :${minute.padStart(2, "0")}`;
    case "daily": return `Runs daily at ${time}`;
    case "weekly": return `Runs every ${daysOfWeek.map(d => dayNames[d] || d).join(", ")} at ${time}`;
    case "monthly": return `Runs on day ${dayOfMonth} of every month at ${time}`;
  }
}

const CronBuilder = ({ value, onChange }: CronBuilderProps) => {
  const parsed = parseCron(value);
  const [frequency, setFrequency] = useState<Frequency>(parsed.frequency);
  const [minute, setMinute] = useState(parsed.minute);
  const [hour, setHour] = useState(parsed.hour);
  const [dayOfMonth, setDayOfMonth] = useState(parsed.dayOfMonth);
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(parsed.daysOfWeek);

  useEffect(() => {
    onChange(buildCron(frequency, minute, hour, dayOfMonth, daysOfWeek));
  }, [frequency, minute, hour, dayOfMonth, daysOfWeek]);

  return (
    <div className="space-y-4 rounded-lg border border-border p-4 bg-muted/30">
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">Frequency</Label>
        <RadioGroup value={frequency} onValueChange={(v) => setFrequency(v as Frequency)} className="flex flex-wrap gap-3">
          {([
            ["every_10_minutes", "Every 10 minutes"],
            ["hourly", "Hourly"],
            ["daily", "Daily"],
            ["weekly", "Weekly"],
            ["monthly", "Monthly"],
          ] as const).map(([val, label]) => (
            <div key={val} className="flex items-center gap-1.5">
              <RadioGroupItem value={val} id={`freq-${val}`} />
              <Label htmlFor={`freq-${val}`} className="text-sm cursor-pointer">{label}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {frequency !== "every_10_minutes" && (
        <div className="flex flex-wrap gap-4">
          {frequency !== "hourly" && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Hour</Label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Minute</Label>
            <Select value={minute} onValueChange={setMinute}>
              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MINUTES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {frequency === "weekly" && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Days</Label>
          <ToggleGroup
            type="multiple"
            value={daysOfWeek}
            onValueChange={(v) => v.length > 0 && setDaysOfWeek(v)}
            className="justify-start"
          >
            {DAYS_OF_WEEK.map((d) => (
              <ToggleGroupItem key={d.value} value={d.value} size="sm" className="text-xs px-2.5">
                {d.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {frequency === "monthly" && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Day of month</Label>
          <Input
            type="number"
            min={1}
            max={28}
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(e.target.value)}
            className="w-20"
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground italic">
        {describeCron(frequency, minute, hour, dayOfMonth, daysOfWeek)}
      </p>
    </div>
  );
};

export default CronBuilder;
